import { BaseComponent, CartItem, Component, Prop, Ref, SimpleEvent, Watch } from "@eastsideco/salvo-ts";
import { ThemeComponent } from "@/theme/ThemeComponent";
import { cart } from "@/theme/Cart";
import { setVariantUrlParam, getQuantityMax, formatPrice} from "@/utils/products";
import { ISimpleEvent } from "strongly-typed-events"; // TODO
import { ProductOptionPicker } from "./ProductOptionPicker";
import { QuantitySpinner } from "../QuantitySpinner/QuantitySpinner";
import { ProductMiniCart } from "./ProductMiniCart";

interface Subscriptions extends HTMLElement {
    enabled: boolean;
    domain: string;
    customerParam: string;
    rechargeProduct: [{
        id: number;
        value: number;
    }];
}

/**
 * A form which allows customers to select product options and then
 * add the matching variant and quantity to the cart.
 */
@Component
export class ProductForm extends ThemeComponent {
    addScrollListener: (() => void)|null = null;

    @Prop product!: ShopifyProduct;
    @Prop simplified!: boolean;
    @Prop optionStyle!: 'dropdown'|'buttons';
    @Prop variantsQty!: string;
    @Prop strings!: string;
    @Prop breakpoint!: string;
    @Prop subscriptions!: Subscriptions;
    @Ref options!: ProductOptionPicker[];
    @Ref submitButton!: HTMLButtonElement;
    @Ref soldOutButton!: HTMLButtonElement;
    @Ref error!: HTMLElement;
    @Ref price!: HTMLElement[];
    @Ref quantity!: QuantitySpinner|null;
    @Ref miniCart!: ProductMiniCart;
    @Ref miniCartNewItem!: HTMLElement;
    @Ref miniCartTotal!: HTMLElement[];
    @Ref() checkoutButton!: HTMLAnchorElement;
    @Ref({ required: false }) shippingFrequency!: HTMLSelectElement;
    @Ref({ required: false }) shippingFrequencyUnit!: HTMLSelectElement;
    @Ref({ required: false }) subscriptionOneTime!: HTMLInputElement;
    @Ref({ required: false }) subscriptionRecurring!: HTMLInputElement;
    @Ref({ required: false }) subscriptionDiscount!: HTMLSpanElement;

    @Ref({required: false}) tests!: HTMLElement[];

    protected loading: boolean = false;
    protected selectedVariant: ShopifyVariant|null = null;
    protected quantityVal: number = 1;
    protected currentPrice: string|null = '0';
    protected maxQty: string = '1';
    protected selectedCartQty: number = 0;
    protected extraDetails: HTMLElement|null = null;
    protected footer: HTMLElement|null = null;

    init() {
        for (const opt of this.options) {
            this.$subscribe(opt.onSelectedVariantChange, (variant) => this.selectedVariant = variant);
        }

        this.selectedVariant = this.options[0].selectedVariant;

        // Quantity Funtions
        this.setQuantityMax();
        this.$subscribe(cart.onChange, () => {this.setQuantityMax();});
        this.checkQuantityChange();

        if (this.subscriptions && this.subscriptions.enabled) {
            this.checkBasket();
            this.$subscribe(cart.onChange, () => { this.checkBasket(); });
        }

        this.$listen(this.submitButton, 'click', () => {
            this.addToCart();
        });

        this.$listen(this.subscriptionOneTime, 'click', () => {
            this.rechargeCheck();
            this.setQuantityMax();
            this.checkBasket();
        });

        this.$listen(this.subscriptionRecurring, 'click', () => {
            this.rechargeCheck();
            this.setQuantityMax();
            this.checkBasket();
        });

        if (!this.addScrollListener) {
            this.addScrollListener = () => this.handleSticky();
        }

        window.addEventListener('resize', this.addScrollListener);
        window.addEventListener('scroll', this.addScrollListener);
        window.addEventListener('orientationchange', this.addScrollListener);

        this.$subscribe(cart.onChange, () => { this.updateMiniCartSubTotal(); });

    }

    protected showError(msg: string|null): void {
        this.error.textContent = msg;
        this.error.classList.add('is-active');
        this.error.style.display = msg ? '' : 'none';
    }

    @Watch('selectedVariant')
    protected handleVariantChanged() {
        setVariantUrlParam(this.selectedVariant?.id || null);
        this.setQuantityMax();
        this.handlePrice();
    }

    @Watch('loading')
    protected handleLoading() {
        this.submitButton.disabled = this.loading;
    }

    /**
     *  Update Price;
     * @returns
     */
    handlePrice() {
        if(this.selectedVariant ===  null) { return; }

        const moneyFormat = this.getString('moneyFormat');
        const quantityCheck = this.selectedVariant.price * this.quantityVal;
        const updatedPrice = formatPrice(quantityCheck, moneyFormat);

        this.currentPrice = updatedPrice;

        if (this.currentPrice === null) { return; }

        for (const price of this.price) {
            price.innerHTML = this.currentPrice;
            this.submitButton.innerHTML = `${this.getString('add')} - ${price.textContent}`;
        }
    }

    /**
     * Set maximum variant quantity on variant change.
     */
    protected setQuantityMax() {
        this.maxQty = getQuantityMax(this.selectedVariant?.id || null, this.variantsQty);
        this.selectedCartQty = this.checkCartQty(this.selectedVariant);

        if (this.selectedCartQty > 0) {
            this.maxQty = String(~~this.maxQty - this.selectedCartQty);
        }

        this.handleQtyInput();
        this.handleQtyButtons();

        if (~~this.maxQty === 0) {
            this.handleOutOfStock();
            this.quantity?.input.setAttribute('max', this.maxQty);
            this.quantity?.classList.add('visually-hidden');
            return;
        }

        this.quantity?.input.setAttribute('max', this.maxQty);

        if (this.subscriptionRecurring && this.subscriptionRecurring.checked) {
            this.quantity?.classList.add('visually-hidden');
        } else {
            this.quantity?.classList.remove('visually-hidden');
        }

        this.handleInStock();
    }

    handleQtyInput() {
        const qtyInput = this.quantity?.input.value;

        if (qtyInput && (~~qtyInput > ~~this.maxQty)) {
            if (!this.quantity) { return; }
            this.quantity.input.value = this.maxQty;
            return;
        }
    }

    handleQtyButtons() {
        if (!this.quantity) { return; }

        if (~~this.quantity.input.value >= ~~this.maxQty) {
            this.quantity.increaseButton.disabled = true;
            return;
        }

        this.quantity.increaseButton.disabled = false;
    }

    handleSticky() {
        // Find other submit button & footer outside of component
        this.extraDetails = document.querySelector('[data-simplified="false"]');
        this.footer = document.querySelector('[data-ref="footer"]');
        const isActive = 'is-active';
        let checkBreakpoint = null;

        if (this.breakpoint) {
            checkBreakpoint = window.matchMedia(`(max-width: ${this.breakpoint})`);
        }

        if (!this.extraDetails || !this.simplified) { return; }

        const bottom = this.extraDetails.offsetTop + this.extraDetails.offsetHeight;

        if (window.scrollY > bottom) {
            this.classList.add(isActive);

            if (this.footer && checkBreakpoint && checkBreakpoint.matches) {
                this.footer.classList.add(isActive);
            }
        } else {
            this.classList.remove(isActive);

            if (this.footer && checkBreakpoint) {
                this.footer.classList.remove(isActive);
            }
        }
    }

    /**
     * Check if selected variant is in cart? get quanity.
     * @param variant - selected variant.
     * @returns
     */
    protected checkCartQty(variant: ShopifyVariant|null) {
        if (variant === null || cart.items.length === 0) { return 0; }

        let quantity = 0;
        for (const item of cart.items) {
            if (item.id === variant.id) {
                quantity = item.quantity;
            }
        }

        return quantity;
    }

    /**
     * Check if recharge product is already in cart
     * Reference against linked product variant id's
     * @returns
    */
    protected checkBasket() {
        if (cart.items.length === 0) { return; }
        if (this.selectedVariant ===  null) { return; }

        if (this.subscriptions && this.subscriptions.enabled) {
            const rechargeId = this.subscriptions.rechargeProduct.filter((el) => el.id === this.selectedVariant?.id);
            let subscription = false;

            for (const item of cart.items) {
                if (item.id === rechargeId[0].value) {
                    if (item.properties && item.properties.shipping_interval_unit_type) {
                        subscription = true;
                        break;
                    }
                }
            }

            if (subscription && this.subscriptionRecurring && this.subscriptionRecurring.checked) {
                this.submitButton.disabled = true;
                this.submitButton.classList.add('button--disabled');
                this.submitButton.innerHTML = this.getString('rechargeSubscription');
            }

            return subscription;
        }
    }

    /**
     * If quantity switcher exists, update Quantity on value change.
     */
    protected checkQuantityChange() {
        if (this.quantity === null) { return; }
        this.$subscribe(this.quantity.onChange, (value) => {
            this.quantityVal = value;
            this.handlePrice();
        });
    }

    protected addToRecharge() {
        const variant = this.selectedVariant;
        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        let cartJson;

        if (this.subscriptionOneTime && this.subscriptionOneTime.checked) {
            cartJson = {
                id: variant.id,
                quantity: this.quantityVal,
                [`autodeliver_${this.product.id}`]: 'onetime'
            };
        } else {
            const rechargeId = this.subscriptions.rechargeProduct.filter((el) => el.id === variant.id);
            let discount = '';

            if (this.subscriptionDiscount) {
                discount = `${this.subscriptionDiscount.dataset.total}`;
            }

            cartJson = {
                id: rechargeId[0].value,
                quantity: this.quantityVal,
                properties: {
                    // properties require underscore, not camelCasing
                    /* eslint-disable */
                    shipping_interval_unit_type: `${this.shippingFrequencyUnit.value}`,
                    shipping_interval_frequency: `${this.shippingFrequency.value}`,
                    discountPercentage: discount
                    /* eslint-enable */
                },
                [`autodeliver_${this.product.id}`]: 'autodeliver',
            };
        }

        cart.addItem(cartJson).catch((event: unknown | null) => {
            if (event instanceof Error) {
                this.showError(event.message);
            }
        }).catch((event: unknown | null) => {
            if (event instanceof Error) {
                this.showError(event.message);
            }
        });

        this.rechargeCheck();
        this.loading = false;
        document.dispatchEvent(new Event('cartOpen'));
        document.dispatchEvent(new Event('cartAddedItem'));
        this.updateCartPopOut(variant);
    }

    protected getCookie(name: string) {
        const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
        if (match) {return match[2];}
    }

    protected rechargeCheck() {
        if (!this.subscriptions.enabled) { return; }
        let checkoutUrl = '/checkout';

        if (this.subscriptionRecurring && this.subscriptionRecurring.checked || this.checkBasket()) {
            this.quantity?.classList.add('visually-hidden');

            checkoutUrl = `https://checkout.rechargeapps.com/r/checkout`;
            checkoutUrl += `?myshopify_domain=${this.subscriptions.domain}`;
            checkoutUrl += `&cart_token=${this.getCookie('cart')}`;

            if (this.subscriptions.customerParam) {
                checkoutUrl += `&${this.subscriptions.customerParam}`;
            }
        } else {
            this.quantity?.classList.remove('visually-hidden');
        }

        this.checkoutButton.href = checkoutUrl;
    }

    /**
     * Handle Add to Cart.
     */
    protected addToCart() {
        this.loading = true;

        if (this.subscriptions && this.subscriptions.enabled) {
            this.addToRecharge();
            return;
        }

        const variant = this.selectedVariant;
        if (!variant) {
            this.showError(`Please select an option.`);
            return;
        }

        /* eslint-disable */
        cart.addItem({
            id: variant.id,
            quantity: this.quantityVal
        }).then((data) => {
            document.dispatchEvent(new Event('cartAddedItem'));
            this.updateCartPopOut(variant);
            document.dispatchEvent(new Event('cartOpen'));
        }).catch((event: unknown | null) => {
            if (event instanceof Error) {
                this.showError(event.message);
            }
        });
        /* eslint-enable */

        this.loading = false;
    }

    // update the sub total of the Mini Cart
    updateMiniCartSubTotal() {
        if (cart.totalPrice) {
            let cartTotalPrice;
            if (this.subscriptions && this.subscriptions.enabled) {
                cartTotalPrice = cart.totalPrice;
            } else if (this.selectedVariant != null) {
                const currentProductPrice = this.selectedVariant.price * this.quantityVal;
                cartTotalPrice = cart.totalPrice + currentProductPrice;
            } else {
                cartTotalPrice = cart.totalPrice;
            }

            for (const cartTotal of this.miniCartTotal) {
                cartTotal.innerHTML = `
                    ${formatPrice(cartTotalPrice, String(JSON.parse(this.strings).moneyFormat))}
                `;
            }
        }
    }

    /**
     * Update the added to cart pop out with selected variant
     * @param variant - selected variant.
     * @returns
     */
    protected updateCartPopOut(variant: ShopifyVariant) {
        this.$log.info('updateCartPopOut here');
        let variants = '';

        if (variant.option1 !== 'Default Title' && variant.option1 !== null) {
            variants += `<p class="mini-cart__cart-item__details-title--variant">${variant.option1}</p>`;
        }

        if (variant.option2 !== 'Default Title' && variant.option2 !== null) {
            variants += `<p class="mini-cart__cart-item__details-title--variant">${variant.option2}</p>`;
        }

        if (variant.option3 !== 'Default Title' && variant.option3 !== null) {
            variants += `<p class="mini-cart__cart-item__details-title--variant">${variant.option3}</p>`;
        }


        const row = `
            <div class="mini-cart__cart-item">
                <div class="mini-cart__cart-item__image object-cover">
                    <img src="${this.product.featured_image}" alt="${this.product.title}">
                </div>
                <div class="mini-cart__cart-item__details">
                    <h3 class="mini-cart__cart-item__details-title">${this.product.title}</h3>
                    
                    <p class="mini-cart__cart-item__details-price">
                        ${ formatPrice(variant.price, String(JSON.parse(this.strings).moneyFormat))}
                    </p>

                    ${variants}
                </div>
            </div>
        `;

        if (this.miniCart) {
            this.miniCartNewItem.innerHTML = `${row}`;
            this.miniCart.classList.add('is-active');

            let cartTotalPrice;
            if (this.subscriptions && this.subscriptions.enabled) {
                cartTotalPrice = cart.totalPrice;
            } else if (this.selectedVariant != null) {
                const currentProductPrice = this.selectedVariant.price * this.quantityVal;
                cartTotalPrice = cart.totalPrice + currentProductPrice;
            } else {
                cartTotalPrice = cart.totalPrice;
            }

            for (const cartTotal of this.miniCartTotal) {
                cartTotal.innerHTML = `
                    ${formatPrice(cartTotalPrice, String(JSON.parse(this.strings).moneyFormat))}
                `;
            }
        }
    }

    /**
     * Diasable AdToCart if variant is out of stock.
     */
    protected handleOutOfStock() {
        this.submitButton.disabled = true;
        this.submitButton.classList.add('button--disabled', 'is-hidden');
        this.submitButton.innerHTML = this.getString('soldOut');
        this.soldOutButton.classList.remove('is-hidden');
    }

    /**
     * Enable AddToCart.
     */
    protected handleInStock() {
        this.submitButton.disabled = false;
        this.submitButton.classList.remove('button--disabled', 'is-hidden');
        this.submitButton.innerHTML = this.getString('add');
        this.soldOutButton.classList.add('is-hidden');
        this.handlePrice();
    }

    /**
     * Get Language string set within the product template.
     * @param string
     * @returns
     */
    protected getString(string: string) {
        const strings = JSON.parse(this.strings);
        return strings[string];
    }
}
